<script setup>
import { onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useItemStore } from "@/stores/item";
import router from "@/router";

const authStore = useAuthStore();
const itemStore = useItemStore();

onMounted(() => {
  // Only load items if user is authenticated
  if (authStore.user._id) {
    itemStore.load();
  }
});

const formatPrice = (price) => {
  return price.toFixed(2) + " " + authStore.user.currencySymbol;
};
</script>

<template>
  <div class="home">
    <div class="row">
      <div class="col-12">
        <h1 class="mb-4">{{ $t("title") }}</h1>
        <p class="lead mb-5">{{ $t("homePage.description") }}</p>
      </div>
    </div>

    <div class="row" v-if="authStore.user._id">
      <!-- Ingredients List -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-primary text-white">
            <h3 class="card-title mb-0">
              <i class="fas fa-apple-alt me-2"></i>
              {{ $t("ingredient.plural") }}
            </h3>
          </div>
          <div class="card-body p-0">
            <div
              v-if="itemStore.ingredients.length === 0"
              class="p-3 text-center text-muted"
            >
              <i class="fas fa-apple-alt fa-2x mb-2"></i>
              <p>
                {{ $t("ingredient.list") }} -
                {{ $t("homePage.ingredients.emptyState") }}
              </p>
              <router-link
                to="/ingredient/new"
                class="btn btn-outline-primary btn-sm"
              >
                {{ $t("ingredient.create") }}
              </router-link>
            </div>
            <div v-else class="list-group list-group-flush">
              <div
                v-for="ingredient in itemStore.ingredients"
                :key="ingredient._id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                @click="router.push('/ingredient/' + ingredient._id)"
                style="cursor: pointer"
              >
                <div>
                  <h6 class="mb-1 fw-bold">{{ ingredient.name }}</h6>
                  <small class="text-muted"
                    >{{ ingredient.servingQuantity }}
                    {{ ingredient.unit }}</small
                  >
                </div>
                <div class="text-end">
                  <span class="badge bg-success fs-6">
                    {{
                      formatPrice(
                        ingredient.unitPrice / ingredient.servingQuantity
                      )
                    }}
                    / {{ ingredient.unit }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="itemStore.ingredients.length > 0" class="card-footer">
            <router-link
              to="/ingredients"
              class="btn btn-outline-primary btn-sm"
            >
              {{ $t("homePage.ingredients.viewAll") }}
            </router-link>
          </div>
        </div>
      </div>

      <!-- Recipes List -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-success text-white">
            <h3 class="card-title mb-0">
              <i class="fas fa-utensils me-2"></i>
              {{ $t("recipe.plural") }}
            </h3>
          </div>
          <div class="card-body p-0">
            <div
              v-if="itemStore.recipes.length === 0"
              class="p-3 text-center text-muted"
            >
              <i class="fas fa-utensils fa-2x mb-2"></i>
              <p>
                {{ $t("recipe.list") }} -
                {{ $t("homePage.recipes.emptyState") }}
              </p>
              <router-link
                to="/recipe/new"
                class="btn btn-outline-success btn-sm"
              >
                {{ $t("recipe.create") }}
              </router-link>
            </div>
            <div v-else class="list-group list-group-flush">
              <div
                v-for="recipe in itemStore.recipes"
                :key="recipe._id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                @click="router.push('/recipe/' + recipe._id)"
                style="cursor: pointer"
              >
                <div>
                  <h6 class="mb-1 fw-bold">{{ recipe.name }}</h6>
                  <small class="text-muted"
                    >{{ recipe.servingQuantity }} {{ recipe.unit }}</small
                  >
                </div>
                <div class="text-end">
                  <span class="badge bg-info fs-6">
                    {{
                      formatPrice(itemStore.computeRecipeCost(recipe._id, true))
                    }}
                    / {{ recipe.unit }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="itemStore.recipes.length > 0" class="card-footer">
            <router-link to="/recipes" class="btn btn-outline-success btn-sm">
              {{ $t("homePage.recipes.viewAll") }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
